import { runModelWithoutNode, type ModelRunObservation } from '@/api/runModel.api';
import { ModelRunError } from '@/api/errors';
import { ModelType } from '@/enums/model-type.enum';
import { useWorkflowStore } from '@/state/workflow.state';
import { assetUrl } from '@/utils/assetUrls';
import { validateFile } from '@/utils/nodeInputValidation';
import { track } from '@/utils/analytics';
import { CompositorAiError, CompositorAiErrorCode } from './compositorAiError';
import { prepareCroppedLayerImages } from './croppedLayerImages.utils';
import type { ModelRunCredits } from '@/api/modelPrediction';
import type { CompositorImageSource } from '@/components/Recipe/Views/CompositorView';
import type { ResolvedModelPrice } from '@/components/Nodes/Utils';
import type { CompositorAiRun, CompositorAiTarget } from '@/components/Recipe/CompositorAiTasks/CompositorAiTasks';
import type { FileKind, LayerId } from 'web';

export const MAX_DECOMPOSE_LAYER_COUNTS = 10;

export const DECOMPOSE_MODELS = [
  {
    id: 'qwen',
    name: 'fal-ai/qwen-image-layered',
    version: 'fal-ai/qwen-image-layered',
    service: ModelType.FalImported,
    label: 'Qwen Image Layered',
    isImported: false,
    supportsLayerCount: true,
    outputLayout: 'full-canvas',
  },
  {
    id: 'seedream',
    name: 'bytedance/seedream/v5/pro/layerize',
    version: 'bytedance/seedream/v5/pro/layerize',
    service: ModelType.FalImported,
    label: 'Seedream 5.0 Pro Layerize',
    isImported: false,
    supportsLayerCount: false,
    outputLayout: 'cropped',
  },
] as const;

export type DecomposeModelId = (typeof DECOMPOSE_MODELS)[number]['id'];

export function getDecomposeModel(modelId: DecomposeModelId) {
  const model = DECOMPOSE_MODELS.find(({ id }) => id === modelId);
  if (!model) throw new Error(`Unknown decompose model: ${modelId}`);
  return model;
}

export function getDecomposeParams(model: DecomposeModelId, numberOfLayers?: number) {
  if (!getDecomposeModel(model).supportsLayerCount) {
    return {};
  }
  return {
    num_layers: numberOfLayers,
    output_format: 'png',
    enable_safety_checker: true,
    acceleration: 'regular',
    guidance_scale: 5,
    num_inference_steps: 28,
  };
}

export interface DecomposeLayerParams {
  model: DecomposeModelId;
  numberOfLayers?: number;
  groupName: string;
}

export type DecomposeImages = Extract<FileKind, { type: 'image' }>[];
export type DecomposeRunner = (
  request: CompositorAiTarget & Pick<DecomposeLayerParams, 'model' | 'numberOfLayers'> & { runId: string },
  observation?: ModelRunObservation,
) => Promise<DecomposeImages>;

export interface DecomposeOptions {
  groupName: string;
  getPrice: (model: DecomposeModelId, numberOfLayers?: number) => ResolvedModelPrice | undefined;
}

export function isValidLayerCount(value: number | undefined): value is number {
  return value !== undefined && Number.isInteger(value) && value >= 2 && value <= MAX_DECOMPOSE_LAYER_COUNTS;
}

export function createDecomposeAction({ groupName, getPrice }: DecomposeOptions) {
  return {
    defaultModel: DECOMPOSE_MODELS[0].id,
    defaultLayerCount: 2,
    getPrice,
    createRun(model: DecomposeModelId, numberOfLayers?: number): CompositorAiRun | undefined {
      const supportsLayerCount = getDecomposeModel(model).supportsLayerCount;
      if (supportsLayerCount && !isValidLayerCount(numberOfLayers)) {
        return undefined;
      }
      return {
        action: 'decompose-layer',
        params: { model, groupName, ...(supportsLayerCount ? { numberOfLayers } : {}) },
      };
    },
  };
}

export type DecomposeAction = ReturnType<typeof createDecomposeAction>;

/** Runs inputs prepared by createDecomposeAction; results belong to compositor assets. */
export function createDecomposeRunner(
  getSource: (layerId: LayerId) => CompositorImageSource | undefined,
  onCredits?: (credits: ModelRunCredits) => void,
): DecomposeRunner {
  return async ({ nodeId, layerId, runId, model: modelId, numberOfLayers }, observation) => {
    const model = getDecomposeModel(modelId);
    const { recipe: workflow, resolvedAssets } = useWorkflowStore.getState();
    if (!workflow.id || !Number.isFinite(workflow.version))
      throw new CompositorAiError(CompositorAiErrorCode.WorkflowRequired);
    const source = getSource(layerId);
    if (!source || !(source.file.width > 0) || !(source.file.height > 0))
      throw new CompositorAiError(CompositorAiErrorCode.SourceUnavailable);
    const imageUrl = assetUrl(source.file, resolvedAssets);
    // Computed PNG data URLs use the existing Fal adapter's replaceBase64WithUrls upload path.
    if (!imageUrl || (!imageUrl.startsWith('https://') && !imageUrl.startsWith('data:image/'))) {
      throw new CompositorAiError(CompositorAiErrorCode.SourceNotUploaded);
    }
    const startedAt = performance.now();
    let returnedCount: number | undefined;
    let outcome = 'failed';
    try {
      const results = await runModelWithoutNode(
        {
          model: {
            name: model.name,
            version: model.version,
            type: model.service,
            isImported: model.isImported,
          },
          input: {
            ...getDecomposeParams(modelId, numberOfLayers),
            image_url: imageUrl,
          },
          recipeId: workflow.id,
          recipeVersion: workflow.version,
          // Intentionally no nodeId: results belong to compositor assets, not a model node.
        },
        onCredits,
        observation,
      );
      returnedCount = Array.isArray(results) ? results.length : undefined;
      if (!Array.isArray(results) || results.length === 0)
        throw new CompositorAiError(CompositorAiErrorCode.EmptyResult);
      let images: DecomposeImages;
      if (model.outputLayout === 'cropped') {
        images = await prepareCroppedLayerImages(results, observation?.signal);
      } else {
        // Keep every result in provider order (background to foreground), even if its count differs.
        images = results.map((result) => {
          const image = validateFile(result);
          if (
            image?.type !== 'image' ||
            !(image.width > 0) ||
            !(image.height > 0) ||
            !Number.isFinite(image.width + image.height)
          ) {
            throw new CompositorAiError(CompositorAiErrorCode.InvalidResult);
          }
          return image;
        });
      }
      outcome = 'success';
      return images.map((image, index) => ({ ...image, name: `Layer ${index + 1}` }));
    } catch (error) {
      if (error instanceof ModelRunError && error.kind === 'canceled') outcome = 'canceled';
      throw error;
    } finally {
      track('compositor_ai_model_run', {
        execution_origin: 'compositor_action',
        action_id: 'decompose-layer',
        run_id: runId,
        workflow_id: workflow.id,
        node_id: nodeId,
        layer_id: layerId,
        model_id: model.name,
        source_kind: source.kind,
        image_width: source.file.width,
        image_height: source.file.height,
        layer_count: returnedCount,
        outcome,
        duration_ms: Math.round(performance.now() - startedAt),
      });
    }
  };
}
